import { SpinResponse, SpecialSymbolGroupType } from '@cgs/data-access';
import {
  AbstractGameConfig,
  ComponentNames,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IconWithValuesConfig,
  IGameConfigProvider,
  IGameStateMachineProvider,
  IReelsEngineProvider,
  ReelsEngine,
  SLOT_SESSION_TOKEN,
  SlotSession,
  T_IGameConfigProvider,
  T_ISlotGameEngineProvider,
} from '@cgs/machines';
import { Container, Random } from '@cgs/wge';
import { FreeSpinEventType } from '../corsica-free-spins.types';
import { createIconValueDescription } from './corsica-icon-values.helper';
import { IconValueDescription } from './corsica-icon-values.types';

export class CorsicaIconWithValuesProvider {
  protected readonly _reelsEngine: ReelsEngine;
  protected readonly _valueDescriptions: Map<number, IconValueDescription> = new Map();

  private _gameStateMachine: GameStateMachine<SpinResponse>;
  private readonly _gameConfig: AbstractGameConfig;
  private readonly _slotSession: SlotSession;
  private readonly _rand: Random = new Random();

  constructor(container: Container) {
    this._gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
    this._reelsEngine =
      container.resolve<IReelsEngineProvider>(T_ISlotGameEngineProvider).reelsEngine;
    this._gameConfig = container.resolve<IGameConfigProvider>(T_IGameConfigProvider).gameConfig;
    this._slotSession = container.resolve<SlotSession>(SLOT_SESSION_TOKEN);

    this._gameStateMachine.stopping.entered.listen(() => this.setIconPrice());
    this._gameStateMachine.stopping.entered.listen(() => this._valueDescriptions.clear());
    this._gameStateMachine.init.entered.listen((_e) => this.addInitValuesToIcons());
  }

  public getValueDescription(reel: number, line: number): IconValueDescription | null {
    const position = this._reelsEngine.iconAnimationHelper.getTruePosition(reel, line);
    return this._valueDescriptions.get(position) || null;
  }

  public getFakeValueDescription(
    reel: number,
    line: number,
    drawId: number
  ): IconValueDescription | null {
    const config = this.getIconWithValuesConfig();
    const iconConfiguration = config.iconWithValueConfigs?.get(drawId);

    if (!iconConfiguration) {
      return null;
    }

    iconConfiguration.values;

    const randomBaseValue = iconConfiguration.values?.length
      ? iconConfiguration.values[this._rand.nextInt(iconConfiguration.values.length)]
      : 0;

    const value = iconConfiguration.shouldBeMultipliedByBet
      ? randomBaseValue * this._slotSession.totalBet
      : randomBaseValue;

    const position = this._reelsEngine.getPosition(reel, line);

    return createIconValueDescription(value, position, 'multText');
  }

  private addInitValuesToIcons(): void {
    this.getIconValueDescription().forEach((description) => {
      const entity = this._reelsEngine.iconAnimationHelper.getEntitiesByFinalPosition(
        description.position
      )[0];
      entity.addComponent(ComponentNames.IconValue, description);
      entity.addComponent(ComponentNames.NeedUpdate, true);
    });
  }

  private setIconPrice(): void {
    this.getIconValueDescription().forEach((description) => {
      const position = description.position;
      if (this._valueDescriptions.has(position)) {
        this._valueDescriptions.delete(position);
      }
      this._valueDescriptions.set(position, description);
    });
  }

  private getIconValueDescription(): IconValueDescription[] {
    const currentConfig = this.getIconWithValuesConfig();

    if (!currentConfig || !currentConfig.iconWithValueConfigs) {
      return [];
    }

    const specialSymbolGroups = this._gameStateMachine.curResponse.specialSymbolGroups || [];
    const groups = specialSymbolGroups.filter(
      (g) => g.type === SpecialSymbolGroupType.Multiplier && g.subType === FreeSpinEventType.Init
    );

    if (!groups.length) {
      return [];
    }

    return groups.map((group) => {
      const position = group.positions?.[0];

      if (position === undefined) {
        throw new Error('Position is not defined for group: ' + JSON.stringify(group));
      }

      const value = group.totalWin || 0;
      return createIconValueDescription(value, position, 'multText');
    });
  }

  private getIconWithValuesConfig(): IconWithValuesConfig {
    const config = this._gameConfig.iconWithValuesConfig;
    const freeSpinsInfo = this._gameStateMachine.curResponse.freeSpinsInfo;

    if (freeSpinsInfo) {
      const namedConfig = this._gameConfig.getNamedConfig(freeSpinsInfo.name);
      return namedConfig?.iconWithValuesConfig || config;
    }

    return config;
  }
}
